import { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import { getWatermarkConfig, saveWatermarkConfig } from '@/api/config';

const todayDate = new Date().toISOString().slice(0, 10);

type WatermarkField = 'userAccount' | 'companyName' | 'date';

interface FieldConfig {
  enabled: boolean;
}

interface WatermarkConfig {
  userAccount: FieldConfig;
  companyName: FieldConfig;
  date: FieldConfig;
}

// Fixed display values (not editable)
const FIELD_VALUES: Record<WatermarkField, string> = {
  userAccount: 'agent@example.com',
  companyName: 'Pacific Vision Trading Co., Ltd.',
  date: todayDate,
};

const FIELD_META: { key: WatermarkField; label: string; icon: string; desc: string; sampleNote?: string }[] = [
  {
    key: 'userAccount',
    label: '用户账号',
    icon: 'ri-user-line',
    desc: '自动显示当前登录用户的邮箱地址，可识别外传来源',
    sampleNote: '动态字段，随登录账号自动变化',
  },
  {
    key: 'companyName',
    label: '公司名称',
    icon: 'ri-building-line',
    desc: '当前代理商的公司名称',
    sampleNote: '示例：Pacific Vision Trading Co., Ltd.（实际显示各代理商自己的公司名称）',
  },
  {
    key: 'date',
    label: '访问日期',
    icon: 'ri-calendar-line',
    desc: '预览文档时的当天日期，实时更新',
    sampleNote: '动态字段，显示实际访问日期',
  },

];

// Watermark style — fixed, not user-adjustable
const WM_OPACITY = 0.12;
const WM_FONT_SIZE = 15; // preview px (scaled)
const WM_ROTATION = -35;

const FILE_TYPE_POLICIES = [
  {
    icon: 'ri-file-pdf-line',
    label: 'PDF 文件',
    watermark: '在线强制水印',
    watermarkNote: '在线浏览时强制叠加水印；下载文件是否加水印由上方「下载水印」开关控制',
    watermarkColor: '#dc2626',
    watermarkIcon: 'ri-shield-check-line',
    download: '后台可配置',
    downloadNote: '管理员可在资料管理中单独开启/关闭每份资料的下载权限',
    downloadColor: '#059669',
    downloadIcon: 'ri-settings-3-line',
  },
  {
    icon: 'ri-video-line',
    label: '视频资料',
    watermark: '在线强制水印',
    watermarkNote: '在线播放时强制叠加水印；下载文件是否加水印由上方「下载水印」开关控制',
    watermarkColor: '#dc2626',
    watermarkIcon: 'ri-shield-check-line',
    download: '后台可配置',
    downloadNote: '管理员可在资料管理中单独开启/关闭每份资料的下载权限',
    downloadColor: '#059669',
    downloadIcon: 'ri-settings-3-line',
  },
  {
    icon: 'ri-presentation-line',
    label: 'PPT 文件',
    watermark: '在线水印',
    watermarkNote: '在线浏览时叠加水印',
    watermarkColor: '#dc2626',
    watermarkIcon: 'ri-shield-check-line',
    download: '管理员自行控制',
    downloadNote: 'PPT 文件下载后为原始格式，无法嵌入水印；下载权限由管理员在资料管理中单独配置',
    downloadColor: '#059669',
    downloadIcon: 'ri-settings-3-line',
  },
  {
    icon: 'ri-file-word-line',
    label: 'Word / Excel',
    watermark: '在线水印',
    watermarkNote: '在线浏览时叠加水印',
    watermarkColor: '#dc2626',
    watermarkIcon: 'ri-shield-check-line',
    download: '禁止下载',
    downloadNote: '可编辑文档强制仅支持在线浏览，禁止下载',
    downloadColor: '#dc2626',
    downloadIcon: 'ri-forbid-line',
  },
];

export default function WatermarkPage() {
  const [config, setConfig] = useState<WatermarkConfig>({
    userAccount: { enabled: true },
    companyName: { enabled: true },
    date: { enabled: true },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // 加载后端配置
  useEffect(() => {
    (async () => {
      try {
        const data = await getWatermarkConfig();
        setConfig({
          userAccount: { enabled: data.WM_USEACCOUNT_SHOW },
          companyName: { enabled: data.WM_COMPANY_SHOW },
          date: { enabled: data.WM_ACCESSDATE_SHOW },
        });
      } catch (err) {
        console.error('加载水印配置失败', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggleField = (key: WatermarkField) => {
    setConfig((prev) => ({
      ...prev,
      [key]: { enabled: !prev[key].enabled },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await saveWatermarkConfig({
        WM_USEACCOUNT_SHOW: config.userAccount.enabled,
        WM_COMPANY_SHOW: config.companyName.enabled,
        WM_ACCESSDATE_SHOW: config.date.enabled,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('保存水印配置失败', err);
    } finally {
      setSaving(false);
    }
  };

  const watermarkParts = (Object.keys(FIELD_VALUES) as WatermarkField[])
    .filter((k) => config[k].enabled)
    .map((k) => FIELD_VALUES[k]);

  const watermarkText = watermarkParts.join('   ·   ');

  const rows = 3;
  const cols = 2;

  return (
    <AdminLayout title="水印设置" subtitle="配置文档预览时的动态水印内容">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

        {/* ── Left: Config ── */}
        <div className="space-y-5">

          {/* Info banner */}
          <div className="rounded-xl border px-4 py-3.5 flex items-start gap-3" style={{ background: 'rgba(0,105,189,0.04)', borderColor: 'rgba(0,105,189,0.15)' }}>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5" style={{ background: 'rgba(0,105,189,0.10)' }}>
              <i className="ri-shield-user-line text-sm" style={{ color: '#0069BD' }}></i>
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800">动态水印保护</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                「用户账号」为动态字段，每位代理商看到的水印都包含其自己的登录邮箱，一旦发现文件外传，可立即溯源到具体账号。
              </p>
            </div>
          </div>

          {/* Field list */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-800">水印内容配置</h3>
                <p className="text-xs text-gray-400 mt-0.5">开启的字段将叠加显示在水印中</p>
              </div>
              <button
                onClick={handleSave}
                disabled={saving || loading}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${saved ? 'bg-green-500 text-white' : 'text-white'}`}
                style={saved ? {} : { background: '#0069BD' }}
              >
                {saving ? (
                  <><i className="ri-loader-4-line animate-spin text-sm"></i>保存中...</>
                ) : saved ? (
                  <><i className="ri-checkbox-circle-line text-sm"></i>已保存</>
                ) : (
                  <><i className="ri-save-line text-sm"></i>保存设置</>
                )}
              </button>
            </div>

            <div className="divide-y divide-gray-50">
              {FIELD_META.map((meta) => {
                const isOn = config[meta.key].enabled;
                const isDynamic = meta.key === 'userAccount' || meta.key === 'date';

                return (
                  <div key={meta.key} className={`px-5 py-4 transition-colors ${isOn ? '' : 'opacity-50'}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${isOn ? 'bg-gray-50 border border-gray-200' : 'bg-gray-100'}`}>
                        <i className={`${meta.icon} text-sm ${isOn ? 'text-gray-600' : 'text-gray-400'}`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-semibold text-gray-800">{meta.label}</p>
                          {isDynamic && (
                            <span className="px-1.5 py-0.5 rounded-full text-xs font-bold bg-amber-50 text-amber-600 border border-amber-200 whitespace-nowrap">
                              动态
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 leading-relaxed">{meta.desc}</p>
                        {meta.sampleNote && (
                          <p className="text-xs mt-1.5 font-mono px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-100 text-gray-500 leading-relaxed">
                            {meta.sampleNote}
                          </p>
                        )}
                      </div>

                      {/* Toggle */}
                      <button
                        onClick={() => toggleField(meta.key)}
                        className="w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0 mt-1.5"
                        style={{ background: isOn ? '#0069BD' : '#d1d5db' }}
                      >
                        <span
                          className="absolute w-5 h-5 bg-white rounded-full transition-all duration-200"
                          style={{
                            top: '2px',
                            left: isOn ? '22px' : '2px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
                          }}
                        ></span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Right: Preview ── */}
        <div>
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-800">效果预览</h3>
                <p className="text-xs text-gray-400 mt-0.5">模拟代理商预览文档时的水印效果</p>
              </div>
              <span className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-600 border border-green-200">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>
                实时更新
              </span>
            </div>

            {/* Document preview */}
            <div className="p-5">
              <div className="relative bg-white rounded-lg overflow-hidden border border-gray-200" style={{ aspectRatio: '0.77' }}>
                {/* Simulated document body */}
                <div className="absolute inset-0 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="space-y-1.5">
                      <div className="h-4 bg-gray-200 rounded w-40"></div>
                      <div className="h-2.5 bg-gray-100 rounded w-24"></div>
                    </div>
                    <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                      <i className="ri-file-pdf-line text-gray-300 text-lg"></i>
                    </div>
                  </div>
                  <div className="h-px bg-gray-100 mb-4"></div>
                  <div className="space-y-2 mb-4">
                    {[90, 75, 82, 68, 88, 72].map((w, i) => (
                      <div key={i} className="h-2 bg-gray-100 rounded" style={{ width: `${w}%` }}></div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="aspect-video bg-gray-100 rounded-lg"></div>
                    <div className="space-y-2 pt-1">
                      {[85, 70, 90, 60].map((w, i) => (
                        <div key={i} className="h-2 bg-gray-100 rounded" style={{ width: `${w}%` }}></div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[80, 65, 75].map((w, i) => (
                      <div key={i} className="h-2 bg-gray-100 rounded" style={{ width: `${w}%` }}></div>
                    ))}
                  </div>
                </div>

                {/* Watermark overlay */}
                {watermarkText && (
                  <div className="absolute inset-0 pointer-events-none overflow-hidden select-none">
                    {Array.from({ length: rows }).map((_, r) =>
                      Array.from({ length: cols }).map((_, c) => (
                        <div
                          key={`${r}-${c}`}
                          className="absolute font-sans whitespace-nowrap"
                          style={{
                            fontSize: `${WM_FONT_SIZE}px`,
                            fontWeight: 500,
                            color: `rgba(30,30,30,${WM_OPACITY})`,
                            transform: `rotate(${WM_ROTATION}deg)`,
                            left: `${c * 55 - 8}%`,
                            top: `${r * 22 - 4}%`,
                            letterSpacing: '0.3px',
                          }}
                        >
                          {watermarkText}
                        </div>
                      ))
                    )}
                  </div>
                )}

                {!watermarkText && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-center">
                      <i className="ri-eye-off-line text-2xl text-gray-200 block mb-1"></i>
                      <p className="text-xs text-gray-300">暂无水印内容</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>


        </div>
      </div>
    </AdminLayout>
  );
}